// Service alerts history — down/flap incidents with time + status filters.
// Backed by GET /api/v1/service-incidents (status, since_hours). See the
// top-bar NotificationBell for the live open-count entry point.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { parseTs } from '../lib/time.js';
import { fmtAgo } from '../components/layout/NotificationBell.jsx';

const WINDOWS = [
  { key: '24h', label: 'Last 24h', hours: 24 },
  { key: '7d', label: 'Last 7 days', hours: 168 },
  { key: '30d', label: 'Last 30 days', hours: 720 },
  { key: 'all', label: 'All time', hours: null },
];
const STATUSES = [
  { key: 'open', label: 'Open' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'all', label: 'All' },
];

// These two worked out the zone rule for themselves, correctly, while twenty other call
// sites did not — which is why it now lives in one function (lib/time.js).
function fmtWhen(iso) {
  const d = parseTs(iso);
  if (!d) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function fmtDuration(a, b) {
  const from = parseTs(a);
  if (!from) return '—';
  const start = from.getTime();
  const end = parseTs(b)?.getTime() ?? Date.now();
  const s = Math.max(0, Math.floor((end - start) / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  return m ? `${m}m` : `${s}s`;
}

function Chip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-sm font-medium transition ${
        active ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
      }`}
    >{children}</button>
  );
}

export default function ServiceAlerts() {
  const [win, setWin] = useState('7d');
  const [status, setStatus] = useState('all');
  const hours = WINDOWS.find((w) => w.key === win)?.hours;

  const q = useQuery({
    queryKey: ['service-incidents', 'history', status, win],
    queryFn: () => api.get('/service-incidents', {
      status,
      ...(hours != null ? { since_hours: hours } : {}),
    }),
    refetchInterval: 30_000,
  });
  const incidents = q.data?.incidents || [];
  const counts = q.data?.open_counts || {};
  const coverage = q.data?.coverage || null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Service alerts</h1>
          <p className="mt-1 text-sm text-gray-500">
            Subscriber lines that went down or flapped.
            {counts.total ? <span className="ml-1 font-medium text-red-600">{counts.total} open now.</span> : null}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-1.5">
          {STATUSES.map((s) => <Chip key={s.key} active={status === s.key} onClick={() => setStatus(s.key)}>{s.label}</Chip>)}
        </div>
        <div className="h-5 w-px bg-gray-200" />
        <div className="flex items-center gap-1.5">
          {WINDOWS.map((w) => <Chip key={w.key} active={win === w.key} onClick={() => setWin(w.key)}>{w.label}</Chip>)}
        </div>
      </div>

      <AlertingCoverage coverage={coverage} />

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {q.isLoading ? (
          <p className="p-6 text-sm text-gray-400">Loading…</p>
        ) : incidents.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm text-gray-400">No alerts in this window.</p>
            {/* THE EMPTY STATE IS THE LIE, so it is the sentence that has to change. "No alerts"
                reads as "nothing went wrong" — and on a fleet where some host cannot raise an
                alert at all, that is the wrong conclusion drawn from the right data. */}
            {coverage?.gaps > 0 && (
              <p className="mx-auto mt-2 max-w-md text-xs text-amber-800">
                …from the hosts that can report. {coverage.gaps === 1 ? 'One host' : `${coverage.gaps} hosts`}
                {' '}cannot, so this is not the whole fleet.
              </p>
            )}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Extension</th>
                <th className="px-4 py-2">Subscriber</th>
                <th className="px-4 py-2">PBX host</th>
                <th className="px-4 py-2">Opened</th>
                <th className="px-4 py-2">Duration</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {incidents.map((i) => (
                <tr key={i.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                      i.kind === 'down' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'
                    }`}>{i.kind}</span>
                  </td>
                  <td className="px-4 py-2 font-mono text-gray-800">{i.extension}</td>
                  <td className="px-4 py-2">
                    {i.subscriberName
                      ? <Link to={`/subscribers/${i.subscriberId}`} className="text-blue-600 hover:underline">{i.subscriberName}</Link>
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-2 text-gray-500">{i.agentId?.slice(0, 8) || '—'}</td>
                  <td className="px-4 py-2 text-gray-600" title={i.openedAt}>{fmtWhen(i.openedAt)}</td>
                  <td className="px-4 py-2 text-gray-600">{fmtDuration(i.openedAt, i.resolvedAt)}</td>
                  <td className="px-4 py-2">
                    {i.status === 'open'
                      ? <span className="inline-flex items-center gap-1 text-red-600"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />open · {fmtAgo(i.openedAt)}</span>
                      : <span className="text-gray-500">resolved</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// WHICH HOSTS COULD HAVE RAISED AN ALERT, said out loud on the page whose whole job is to show
// alerts. Every incident here comes from a registration.up/down pushed over the agent's WebSocket,
// and an Aria host does not mount that endpoint — so it cannot raise one, ever. On screen a host
// in that state is otherwise identical to a host having a quiet week, and "quiet" is the reading
// people take.
//
// Kevin's call, 2026-09-04: accept that Aria hosts are unmonitored for now, and surface it loudly
// rather than build polling on /registrations field names that are still unverified upstream
// (aria-cloud-pbx#45, against a host with zero registrations to check them against).
//
// 'unknown' is deliberately a SEPARATE line and not folded into the count. A host that could not
// be asked may well be streaming events perfectly — every agent older than /capabilities does —
// and announcing a hole that is not there costs the same credibility as missing one that is.
function AlertingCoverage({ coverage }) {
  if (!coverage) return null;
  const none = (coverage.hosts || []).filter((h) => h.alerting === 'none');
  const unknown = (coverage.hosts || []).filter((h) => h.alerting === 'unknown');
  if (!none.length && !unknown.length) return null;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
      {none.length > 0 && (
        <>
          <p className="text-sm font-medium text-amber-900">
            {none.length === 1 ? 'One PBX host cannot raise service alerts.' : `${none.length} PBX hosts cannot raise service alerts.`}
          </p>
          <ul className="mt-1 space-y-0.5">
            {none.map((h) => (
              <li key={h.agent_id} className="text-xs text-amber-800">
                <Link to={`/pbx-hosts/${h.agent_id}`} className="font-medium underline">{h.name}</Link>
                {' — '}{h.reason || 'this host has no event stream'}
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-xs text-amber-800">
            Lines on {none.length === 1 ? 'that host' : 'those hosts'} are not being watched: an
            outage there will not appear on this page, and neither will a recovery. Nothing above is
            evidence they are up.
          </p>
        </>
      )}
      {unknown.length > 0 && (
        <p className={`text-xs text-amber-800${none.length ? ' mt-2' : ''}`}>
          {unknown.length === 1 ? '1 host could not be asked' : `${unknown.length} hosts could not be asked`}
          {' '}whether it reports alerts ({unknown.map((h) => h.name).join(', ')}). That is not the
          same as knowing it does not — most likely it does.
        </p>
      )}
    </div>
  );
}
